import {
  Compass,
  LayoutDashboard,
  Container as ContainerIcon,
  FileText,
  Map,
  Tag,
  Sparkles,
  Search,
  Building2,
  ShieldCheck,
  Calculator,
  type LucideIcon,
} from 'lucide-react';
import type { ShippingDirection } from '@/contexts/DirectionContext';
import type { Tone } from '@/components/wingo/lib/tones';

/**
 * Conținutul centrului de ajutor din portal (pagina /app/help), în română și engleză.
 *
 * Descrie DOAR ce face aplicația azi — verificat în cod pe 05.10.2026. Nu documenta aici
 * funcții care apar în interfață dar nu sunt legate (notificările, „Cere ofertă nouă”,
 * codurile de rezervă 2FA, facturile „În lucru”). Când o pagină se schimbă, actualizează
 * și ghidul ei, altfel clientul primește instrucțiuni greșite.
 */

export type HelpLang = 'ro' | 'en';
export type Localized = Record<HelpLang, string>;

export interface HelpGuide {
  id: string;
  icon: LucideIcon;
  /** Pagina în care se aplică ghidul (butonul „Deschide pagina”). */
  to?: string;
  /** Direcțiile pentru care are sens ghidul; lipsă = ambele. */
  directions?: ShippingDirection[];
  title: Localized;
  summary: Localized;
  steps: Localized[];
  tips?: Localized[];
}

export interface HelpFaq {
  id: string;
  directions?: ShippingDirection[];
  question: Localized;
  answer: Localized;
}

export interface HelpGlossaryTerm {
  id: string;
  term: Localized;
  definition: Localized;
}

export interface HelpStatus {
  id: string;
  tone: Tone;
  /** Culoare CSS care înlocuiește tonul (ex. violetul „hub” al portalului pentru transshipment). */
  color?: string;
  directions: ShippingDirection[];
  label: Localized;
  definition: Localized;
}

export interface HelpShortcut {
  id: string;
  keys: string[];
  label: Localized;
}

export const HELP_GUIDES: HelpGuide[] = [
  {
    id: 'getting-started',
    icon: Compass,
    title: { ro: 'Primii pași', en: 'Getting started' },
    summary: {
      ro: 'Cum e organizat portalul și ce să faci la prima conectare.',
      en: 'How the portal is organised and what to do when you first sign in.',
    },
    steps: [
      {
        ro: 'În stânga ai meniul principal (pe telefon, bara de jos și butonul ☰ din colțul stâng): Dashboard, Containere, Conosamente, Hartă live, Oferte și, pe import, Costuri și Asistentul AI.',
        en: 'The main menu sits on the left (on a phone, the bottom bar and the ☰ button in the top-left corner): Dashboard, Containers, Bills of lading, Live map, Offers and, for import, Costs and the AI assistant.',
      },
      {
        ro: 'În bara de sus alegi firma (sau „Toate firmele”) și, dacă lucrezi și pe import, și pe export, direcția. Paginile se filtrează după alegerea ta.',
        en: 'In the top bar you choose the company (or “All companies”) and, if you work on both import and export, the direction. Pages are filtered by your choice.',
      },
      {
        ro: 'Activează autentificarea în doi pași din „Configurare 2FA”: contul tău vede documente de transport și prețuri.',
        en: 'Turn on two-step verification from “2FA setup”: your account shows shipping documents and prices.',
      },
      {
        ro: 'Turul ghidat îți arată toate acestea pe ecran. Îl poți relua oricând din această pagină.',
        en: 'The guided tour shows you all of this on screen. You can replay it any time from this page.',
      },
    ],
  },
  {
    id: 'dashboard',
    icon: LayoutDashboard,
    to: '/app/dashboard',
    title: { ro: 'Dashboard', en: 'Dashboard' },
    summary: {
      ro: 'Situația pe scurt: containere active, sosiri, plecări de nave și ultimele oferte.',
      en: 'The overview: active containers, arrivals, vessel departures and the latest offers.',
    },
    steps: [
      {
        ro: 'Cardurile de sus arată containerele active pe statusuri, câte sunt în tranzit și pe câte nave, și ce sosește în următoarele 14 zile (pe export: ce pleacă).',
        en: 'The cards at the top show active containers by status, how many are in transit and on how many vessels, and what arrives in the next 14 days (for export: what departs).',
      },
      {
        ro: 'Pe import, al patrulea card arată costurile estimate (navlul ofertat pe B/L-urile în tranzit, pe luna sosirii). Pe export arată containerele descărcate.',
        en: 'For import, the fourth card shows estimated costs (the offered freight on bills of lading in transit, by arrival month). For export it shows discharged containers.',
      },
      {
        ro: '„Containere care vin în curând” și „Bookingurile în tranzit” au buton „Export” pentru Excel. La bookinguri poți sorta apăsând pe capul de coloană.',
        en: '“Containers arriving soon” and “Bookings in transit” have an “Export” button for Excel. In bookings you can sort by clicking a column header.',
      },
      {
        ro: '„Plecări nave”, „Ultima ofertă” și „Timpi de tranzit” te ajută să planifici următoarele transporturi. Apasă pe o ofertă din „Ultimele oferte” ca s-o deschizi.',
        en: '“Vessel departures”, “Latest offer” and “Transit times” help you plan the next shipments. Click an offer in “Latest offers” to open it.',
      },
    ],
  },
  {
    id: 'containers',
    icon: ContainerIcon,
    to: '/app/containers',
    title: { ro: 'Containere', en: 'Containers' },
    summary: {
      ro: 'Toate containerele tale, cu filtre pe status, coloane configurabile, notițe și documente.',
      en: 'All your containers, with status filters, configurable columns, notes and documents.',
    },
    steps: [
      {
        ro: 'Caută după container, B/L, booking, navă, marfă, shipper, port sau notă. Pastilele de status filtrează lista și arată câte containere are fiecare status.',
        en: 'Search by container, bill of lading, booking, vessel, commodity, shipper, port or note. The status chips filter the list and show how many containers each status has.',
      },
      {
        ro: 'Din „Coloane” ascunzi, afișezi și reordonezi coloanele (▲▼); „Reset” revine la configurația inițială.',
        en: 'From “Columns” you hide, show and reorder columns (▲▼); “Reset” goes back to the default layout.',
      },
      {
        ro: '„Export” descarcă în Excel toate rândurile filtrate (de pe toate paginile), cu coloane în plus: tip, marfă, B/L, greutate, porturi.',
        en: '“Export” downloads every filtered row to Excel (across all pages), with extra columns: type, commodity, bill of lading, weight, ports.',
      },
      {
        ro: 'În coloana „Notă” îți scrii o notiță proprie pe container. Se salvează când ieși din câmp (sau cu ⌘/Ctrl + Enter); Esc renunță.',
        en: 'In the “Note” column you write your own note on a container. It saves when you leave the field (or with ⌘/Ctrl + Enter); Esc cancels.',
      },
      {
        ro: 'Apasă pe un container ca să-i vezi detaliile: privire generală, călătoria și evenimentele. „Deschide tracking complet” te duce la urmărirea Yang Ming.',
        en: 'Click a container to see its details: overview, voyage and events. “Open full tracking” takes you to Yang Ming tracking.',
      },
    ],
    tips: [
      {
        ro: 'Filtrul, căutarea, sortarea și coloanele se păstrează automat în acest browser, separat pentru import și export.',
        en: 'The filter, search, sorting and columns are kept automatically in this browser, separately for import and export.',
      },
    ],
  },
  {
    id: 'documents',
    icon: FileText,
    to: '/app/containers',
    directions: ['import'],
    title: { ro: 'Documente (import)', en: 'Documents (import)' },
    summary: {
      ro: 'Avizarea de sosire, cererea de eliberare și Letter of Indemnity, din fișa containerului.',
      en: 'The arrival notice, the release request and the Letter of Indemnity, from the container card.',
    },
    steps: [
      {
        ro: 'Deschide containerul din pagina „Containere” și mergi în tab-ul „Documente”.',
        en: 'Open the container from the “Containers” page and go to the “Documents” tab.',
      },
      {
        ro: '„Avizare de sosire” apare „În așteptare” până aproape de sosire; când devine „Disponibil”, o descarci ca PDF cu „Descarcă”.',
        en: '“Arrival notice” shows “Pending” until close to arrival; once it is “Available”, download the PDF with “Download”.',
      },
      {
        ro: 'Din „Generează un document” obții „Cererea de eliberare” și, pentru B/L-urile Telex sau SWB, „Letter of Indemnity” — ambele ca PDF, completate cu datele containerului.',
        en: 'From “Generate a document” you get the “Release request” and, for Telex or SWB bills of lading, the “Letter of Indemnity” — both as PDFs filled with the container data.',
      },
    ],
  },
  {
    id: 'bills',
    icon: FileText,
    to: '/app/bills',
    title: { ro: 'Conosamente (B/L)', en: 'Bills of lading' },
    summary: {
      ro: 'Conosamentele bookingurilor tale, cu părți, marfă, containere și detalii operaționale.',
      en: 'The bills of lading of your bookings, with parties, cargo, containers and operational details.',
    },
    steps: [
      {
        ro: 'Lista funcționează ca la Containere: căutare, filtre pe status, „Coloane”, „Export” în Excel și paginare.',
        en: 'The list works like Containers: search, status filters, “Columns”, Excel “Export” and paging.',
      },
      {
        ro: 'Apasă pe un B/L ca să vezi ruta, părțile (expeditor, destinatar, notify), marfa cu codul HS, containerele și liniile de marfă.',
        en: 'Click a bill of lading to see the route, the parties (shipper, consignee, notify), the cargo with its HS code, the containers and the cargo lines.',
      },
      {
        ro: 'Poți lăsa o notă pe B/L și câte una pe fiecare container din el.',
        en: 'You can leave a note on the bill of lading and one on each of its containers.',
      },
    ],
  },
  {
    id: 'map',
    icon: Map,
    to: '/app/map',
    title: { ro: 'Hartă live', en: 'Live map' },
    summary: {
      ro: 'Unde sunt acum navele cu containerele tale și ce ai în porturi.',
      en: 'Where the vessels carrying your containers are right now and what sits in ports.',
    },
    steps: [
      {
        ro: 'Lista „În tranzit acum” arată navele cu containerele tale și porturile în care ai containere. Apasă pe o intrare ca harta să meargă la ea.',
        en: 'The “In transit now” list shows the vessels with your containers and the ports where you have containers. Click an entry to move the map to it.',
      },
      {
        ro: 'Apasă pe o navă ca să vezi locația și marfa de la bord, pe un port pentru containerele din port, apoi pe un container pentru istoricul lui.',
        en: 'Click a vessel to see its location and the cargo on board, a port for the containers there, then a container for its history.',
      },
      {
        ro: '„Reîmprospătează” aduce pozițiile cele mai noi.',
        en: '“Refresh” loads the latest positions.',
      },
    ],
  },
  {
    id: 'offers',
    icon: Tag,
    to: '/app/offers',
    title: { ro: 'Oferte', en: 'Offers' },
    summary: {
      ro: 'Tarifele transmise de TLS: navlu, taxe locale și transport, cu valabilitate.',
      en: 'The rates TLS sent you: freight, local charges and transport, with validity.',
    },
    steps: [
      {
        ro: 'Tab-urile „Toate”, „Valabile” și „Expirate” separă ofertele; o ofertă care expiră în cel mult 5 zile e semnalată pe card.',
        en: 'The “All”, “Valid” and “Expired” tabs split the offers; an offer that expires within 5 days is flagged on its card.',
      },
      {
        ro: 'Caută după referință, rută, tip sau preț și sortează după emitere, expirare sau prețul minim.',
        en: 'Search by reference, route, type or price and sort by issue date, expiry or lowest price.',
      },
      {
        ro: '„Vezi detalii” deschide oferta completă, cu toți termenii și condițiile.',
        en: '“View details” opens the full offer, with all terms and conditions.',
      },
      {
        ro: '„Export” descarcă ofertele în Excel, cu câte o coloană pentru fiecare tip de container.',
        en: '“Export” downloads the offers to Excel, with one column for each container type.',
      },
    ],
  },
  {
    id: 'costs',
    icon: Calculator,
    to: '/app/costs',
    directions: ['import'],
    title: { ro: 'Costuri (import)', en: 'Costs (import)' },
    summary: {
      ro: 'Estimarea costurilor pentru transporturile tale și tarifele noastre standard din portul Constanța.',
      en: 'The estimated cost of your shipments and our standard tariffs in the port of Constanța.',
    },
    steps: [
      {
        ro: '„Estimare costuri” arată, pentru fiecare transport pe drum, navlul din oferta ta și cheltuielile locale (THC) estimate la sosire, plus totalurile pe lunile următoare, după ETA.',
        en: '“Cost estimate” shows, for each shipment on the way, the freight from your offer and the estimated local charges (THC) on arrival, plus totals for the coming months by ETA.',
      },
      {
        ro: 'Cheltuielile locale sunt cele din oferta ta; dacă oferta nu le conține, se folosește tariful standard valabil la data sosirii.',
        en: 'Local charges are the ones in your offer; if the offer has none, the standard tariff valid on arrival is used.',
      },
      {
        ro: '„Tarifele noastre” listează cheltuielile locale pe tip și mărime de container și, pe categorii, zilele libere și prețurile pe zi pentru contrastalii și depozitare.',
        en: '“Our tariffs” lists the local charges by container type and size and, by category, the free days and daily rates for demurrage and storage.',
      },
      {
        ro: '„Calculator” estimează un transport nou: alegi mărimea, tipul, numărul de containere și câte zile stau în terminal și până la retur.',
        en: '“Calculator” estimates a new shipment: pick the size, type, number of containers and how many days they stay in the terminal and until return.',
      },
    ],
    tips: [
      {
        ro: 'Sumele sunt estimative și afișate fără TVA; „+ TVA” arată unde se adaugă TVA la facturare. Cheltuielile locale, contrastaliile și depozitarea se facturează în lei, la cursul din ziua facturii.',
        en: 'Amounts are estimates shown excluding VAT; “+ VAT” marks where VAT is added on the invoice. Local charges, demurrage and storage are invoiced in RON at the exchange rate on the invoice date.',
      },
    ],
  },
  {
    id: 'assistant',
    icon: Sparkles,
    to: '/app/ai-chat',
    directions: ['import'],
    title: { ro: 'Asistent AI (import)', en: 'AI assistant (import)' },
    summary: {
      ro: 'Întreabă în cuvinte simple despre containerele și bookingurile tale de import.',
      en: 'Ask in plain words about your import containers and bookings.',
    },
    steps: [
      {
        ro: 'Scrie o întrebare sau alege o sugestie, de exemplu „Ce containere sosesc săptămâna aceasta?”. Enter trimite, Shift + Enter trece pe rândul următor.',
        en: 'Type a question or pick a suggestion, for example “Which containers arrive this week?”. Enter sends, Shift + Enter starts a new line.',
      },
      {
        ro: 'Conversațiile rămân în stânga: le poți fixa, redenumi sau șterge din meniul fiecăreia.',
        en: 'Conversations stay on the left: pin, rename or delete them from each one’s menu.',
      },
    ],
    tips: [
      {
        ro: 'Asistentul poate greși — verifică cifrele importante în paginile Containere și Conosamente.',
        en: 'The assistant can make mistakes — check important figures on the Containers and Bills of lading pages.',
      },
    ],
  },
  {
    id: 'search',
    icon: Search,
    title: { ro: 'Căutare rapidă', en: 'Quick search' },
    summary: {
      ro: 'Găsește orice container, B/L, ofertă, navă sau firmă din orice pagină.',
      en: 'Find any container, bill of lading, offer, vessel or company from any page.',
    },
    steps: [
      {
        ro: 'Apasă ⌘K (Mac) sau Ctrl + K, ori tasta /, sau bara „Caută…” de sus (lupa, pe telefon).',
        en: 'Press ⌘K (Mac) or Ctrl + K, or the / key, or the “Search…” bar at the top (the magnifier, on a phone).',
      },
      {
        ro: 'Scrie câteva caractere, alege cu ↑ ↓ și deschide cu Enter: containerele, B/L-urile și ofertele se deschid direct în fișa lor; o firmă devine firma selectată.',
        en: 'Type a few characters, move with ↑ ↓ and open with Enter: containers, bills of lading and offers open straight in their card; a company becomes the selected company.',
      },
    ],
  },
  {
    id: 'companies',
    icon: Building2,
    title: { ro: 'Firme și direcții', en: 'Companies and directions' },
    summary: {
      ro: 'Lucrezi pentru mai multe firme sau și pe import, și pe export? Așa schimbi vederea.',
      en: 'Working for several companies, or on both import and export? This is how you switch.',
    },
    steps: [
      {
        ro: 'Selectorul „Firmă” din bara de sus filtrează dashboard-ul, containerele, conosamentele, ofertele și căutarea pe o singură firmă sau pe „Toate firmele”.',
        en: 'The “Company” selector in the top bar filters the dashboard, containers, bills of lading, offers and search to one company or to “All companies”.',
      },
      {
        ro: 'Selectorul „Direcție” apare doar dacă contul tău are și import, și export. Statusurile, coloanele și unele pagini diferă între cele două.',
        en: 'The “Direction” selector only appears if your account has both import and export. Statuses, columns and some pages differ between the two.',
      },
      {
        ro: 'Alegerea se păstrează în acest browser până o schimbi.',
        en: 'Your choice is kept in this browser until you change it.',
      },
    ],
  },
  {
    id: 'account',
    icon: ShieldCheck,
    to: '/app/settings',
    title: { ro: 'Cont și securitate', en: 'Account and security' },
    summary: {
      ro: 'Autentificarea în doi pași, alertele de conectare, limba, tema și deconectarea.',
      en: 'Two-step verification, sign-in alerts, language, theme and signing out.',
    },
    steps: [
      {
        ro: '„Configurare 2FA” te ghidează în patru pași: alegi aplicația de autentificare (recomandat) sau emailul, configurezi, introduci codul de 6 cifre și gata.',
        en: '“2FA setup” walks you through four steps: choose an authenticator app (recommended) or email, set it up, enter the 6-digit code and you are done.',
      },
      {
        ro: 'În „Setări” pornești „Notificare la autentificare nouă”: primești un email când contul e accesat de pe un dispozitiv nou.',
        en: 'In “Settings” turn on “New sign-in notification”: you get an email when your account is used from a new device.',
      },
      {
        ro: 'Limba (RO / EN) și tema (luminoasă / întunecată) se schimbă din butoanele din dreapta barei de sus, pe calculator sau tabletă.',
        en: 'Language (RO / EN) and theme (light / dark) are switched from the buttons on the right of the top bar, on a computer or tablet.',
      },
      {
        ro: 'Te deconectezi din cardul cu numele tău, jos în meniul din stânga.',
        en: 'Sign out from the card with your name at the bottom of the left menu.',
      },
    ],
  },
];

export const HELP_STATUSES: HelpStatus[] = [
  {
    id: 'booked',
    tone: 'neutral',
    directions: ['export'],
    label: { ro: 'Rezervat', en: 'Booked' },
    definition: { ro: 'Bookingul e creat; marfa nu e încă la bord.', en: 'The booking exists; the cargo is not on board yet.' },
  },
  {
    id: 'loaded',
    tone: 'brand',
    directions: ['export'],
    label: { ro: 'Încărcat', en: 'Loaded' },
    definition: { ro: 'Containerul a fost încărcat pe navă în Constanța.', en: 'The container was loaded on the vessel in Constanța.' },
  },
  {
    id: 'in_transit',
    tone: 'info',
    directions: ['import', 'export'],
    label: { ro: 'În tranzit', en: 'In transit' },
    definition: {
      ro: 'Containerul e pe nava principală (nava mamă), pe drumul lung.',
      en: 'The container is on the main (mother) vessel, on the long leg.',
    },
  },
  {
    id: 'transshipment',
    tone: 'info',
    color: 'var(--st-hub)',
    directions: ['import', 'export'],
    label: { ro: 'Transshipment', en: 'Transshipment' },
    definition: {
      ro: 'Containerul e în portul de transbordare (de regulă în Turcia) și așteaptă nava următoare.',
      en: 'The container is at the transshipment port (usually in Türkiye), waiting for the next vessel.',
    },
  },
  {
    id: 'black_sea',
    tone: 'warning',
    directions: ['import', 'export'],
    label: { ro: 'Marea Neagră', en: 'Black Sea' },
    definition: {
      ro: 'Containerul e pe nava feeder, între portul de transbordare și Constanța.',
      en: 'The container is on the feeder vessel, between the transshipment port and Constanța.',
    },
  },
  {
    id: 'discharged',
    tone: 'info',
    directions: ['import', 'export'],
    label: { ro: 'Descărcat', en: 'Discharged' },
    definition: {
      ro: 'Containerul a fost descărcat în portul de destinație (pe import, în terminalul din Constanța).',
      en: 'The container was discharged at the destination port (for import, at the Constanța terminal).',
    },
  },
  {
    id: 'in_delivery',
    tone: 'brand',
    directions: ['import'],
    label: { ro: 'În livrare', en: 'In delivery' },
    definition: { ro: 'Containerul a plecat din port spre tine, pe transport terestru.', en: 'The container left the port towards you, by road.' },
  },
  {
    id: 'completed',
    tone: 'success',
    directions: ['import', 'export'],
    label: { ro: 'Finalizat', en: 'Completed' },
    definition: { ro: 'Transportul s-a încheiat.', en: 'The shipment is finished.' },
  },
];

export const HELP_GLOSSARY: HelpGlossaryTerm[] = [
  {
    id: 'bl',
    term: { ro: 'Conosament (B/L)', en: 'Bill of lading (B/L)' },
    definition: {
      ro: 'Documentul de transport maritim emis de linie pentru marfa unui booking. Un booking poate avea mai multe B/L-uri.',
      en: 'The ocean transport document issued by the line for a booking’s cargo. A booking can have several bills of lading.',
    },
  },
  {
    id: 'booking',
    term: { ro: 'Booking', en: 'Booking' },
    definition: {
      ro: 'Rezervarea de spațiu pe navă; grupează containerele și B/L-urile unui transport.',
      en: 'The space reservation on the vessel; it groups the containers and bills of lading of a shipment.',
    },
  },
  {
    id: 'eta-etd',
    term: { ro: 'ETA / ETD', en: 'ETA / ETD' },
    definition: {
      ro: 'Data estimată de sosire / de plecare a navei. Se poate modifica pe parcurs.',
      en: 'Estimated time of arrival / departure of the vessel. It can change along the way.',
    },
  },
  {
    id: 'onboard',
    term: { ro: 'On Board Date', en: 'On Board Date' },
    definition: {
      ro: 'Data la care marfa a fost încărcată la bord în portul de plecare.',
      en: 'The date the cargo was loaded on board at the port of loading.',
    },
  },
  {
    id: 'pol-pod',
    term: { ro: 'POL / POD', en: 'POL / POD' },
    definition: { ro: 'Portul de încărcare / portul de descărcare.', en: 'Port of loading / port of discharge.' },
  },
  {
    id: 'telex-swb',
    term: { ro: 'Telex release / SWB', en: 'Telex release / SWB' },
    definition: {
      ro: 'Eliberarea mărfii fără conosamentul original pe hârtie (Telex) sau cu un Sea Waybill (SWB).',
      en: 'Cargo release without the original paper bill of lading (Telex) or with a Sea Waybill (SWB).',
    },
  },
  {
    id: 'loi',
    term: { ro: 'Letter of Indemnity (LOI)', en: 'Letter of Indemnity (LOI)' },
    definition: {
      ro: 'Scrisoare de garanție cerută la eliberarea mărfii pe Telex sau SWB.',
      en: 'A letter of guarantee required when cargo is released on Telex or SWB.',
    },
  },
  {
    id: 'feeder',
    term: { ro: 'Navă feeder / navă mamă', en: 'Feeder / mother vessel' },
    definition: {
      ro: 'Nava mamă face drumul lung; feederul duce containerele între portul de transbordare și Constanța.',
      en: 'The mother vessel sails the long leg; the feeder carries containers between the transshipment port and Constanța.',
    },
  },
  {
    id: 'freight',
    term: { ro: 'Ocean Freight (navlu)', en: 'Ocean freight' },
    definition: {
      ro: 'Prețul transportului maritim din oferta ta, afișat pe containere și B/L-uri la import.',
      en: 'The ocean transport price from your offer, shown on import containers and bills of lading.',
    },
  },
  {
    id: 'thc',
    term: { ro: 'Cheltuieli locale (THC)', en: 'Local charges (THC)' },
    definition: {
      ro: 'Taxa pe container pentru manipularea în terminal, eliberare, expediție și documentație la sosirea în Constanța.',
      en: 'The per-container charge for terminal handling, release, dispatch and documentation on arrival in Constanța.',
    },
  },
  {
    id: 'demurrage',
    term: { ro: 'Contrastalii (demurrage)', en: 'Demurrage' },
    definition: {
      ro: 'Taxa pe zi pentru folosirea containerului liniei după zilele libere, de la descărcare până la returnarea lui gol.',
      en: "The daily charge for using the carrier's container after the free days, from discharge until it is returned empty.",
    },
  },
  {
    id: 'storage',
    term: { ro: 'Depozitare (storage)', en: 'Storage' },
    definition: {
      ro: 'Taxa pe zi a terminalului după zilele libere, de la descărcare până la ieșirea containerului din terminal.',
      en: 'The daily terminal charge after the free days, from discharge until the container leaves the terminal.',
    },
  },
];

export const HELP_SHORTCUTS: HelpShortcut[] = [
  { id: 'search', keys: ['mod', 'K'], label: { ro: 'Deschide căutarea rapidă', en: 'Open quick search' } },
  { id: 'search-slash', keys: ['/'], label: { ro: 'Deschide căutarea (în afara unui câmp de text)', en: 'Open search (outside a text field)' } },
  { id: 'palette-nav', keys: ['↑', '↓'], label: { ro: 'Mută-te prin rezultatele căutării', en: 'Move through search results' } },
  { id: 'palette-open', keys: ['Enter'], label: { ro: 'Deschide rezultatul ales', en: 'Open the chosen result' } },
  { id: 'escape', keys: ['Esc'], label: { ro: 'Închide căutarea, fișele și meniurile', en: 'Close search, cards and menus' } },
  { id: 'note-save', keys: ['mod', 'Enter'], label: { ro: 'Salvează nota pe container sau B/L', en: 'Save the note on a container or bill of lading' } },
  { id: 'ai-newline', keys: ['Shift', 'Enter'], label: { ro: 'Rând nou în Asistentul AI (Enter trimite)', en: 'New line in the AI assistant (Enter sends)' } },
];

export const HELP_FAQ: HelpFaq[] = [
  {
    id: 'missing-container',
    question: { ro: 'Nu văd un container sau un booking. De ce?', en: 'I can’t see a container or a booking. Why?' },
    answer: {
      ro: 'Verifică întâi firma și direcția din bara de sus: alege „Toate firmele” și direcția potrivită (import / export), apoi șterge căutarea și pune filtrul de status pe „Toate”. Dacă tot nu apare, scrie-i agentului TLS cu care lucrezi: transportul poate fi atribuit altei firme din grupul tău.',
      en: 'First check the company and direction in the top bar: choose “All companies” and the right direction (import / export), then clear the search and set the status filter to “All”. If it still doesn’t show, write to your TLS agent: the shipment may be assigned to another company in your group.',
    },
  },
  {
    id: 'arrival-notice',
    directions: ['import'],
    question: { ro: 'Când pot descărca avizarea de sosire?', en: 'When can I download the arrival notice?' },
    answer: {
      ro: 'Avizarea apare „În așteptare” până aproape de sosirea navei, apoi devine „Disponibil” în tab-ul „Documente” din fișa containerului.',
      en: 'The notice shows “Pending” until shortly before the vessel arrives, then becomes “Available” in the “Documents” tab of the container card.',
    },
  },
  {
    id: 'loi',
    directions: ['import'],
    question: { ro: 'De ce nu pot genera Letter of Indemnity?', en: 'Why can’t I generate a Letter of Indemnity?' },
    answer: {
      ro: 'LOI se generează doar pentru B/L-urile eliberate pe Telex sau SWB. Pentru celelalte, butonul rămâne inactiv.',
      en: 'The LOI can only be generated for bills of lading released on Telex or SWB. For the others the button stays inactive.',
    },
  },
  {
    id: 'assistant-missing',
    question: { ro: 'Unde e Asistentul AI?', en: 'Where is the AI assistant?' },
    answer: {
      ro: 'Asistentul lucrează pe datele de import, așa că apare în meniu doar când direcția selectată e „Import”.',
      en: 'The assistant works on import data, so it only appears in the menu when the selected direction is “Import”.',
    },
  },
  {
    id: 'excel',
    question: { ro: 'Cum export datele în Excel?', en: 'How do I export data to Excel?' },
    answer: {
      ro: 'Butonul „Export” din Containere, Conosamente, Oferte și din cardurile dashboard-ului descarcă un fișier .xlsx cu rândurile filtrate, de pe toate paginile — inclusiv coloanele pe care le-ai ascuns în tabel.',
      en: 'The “Export” button on Containers, Bills of lading, Offers and the dashboard cards downloads an .xlsx file with the filtered rows from every page — including the columns you hid in the table.',
    },
  },
  {
    id: 'saved-filters',
    question: { ro: 'Se păstrează filtrele și coloanele mele?', en: 'Are my filters and columns kept?' },
    answer: {
      ro: 'Da, automat, în browserul în care lucrezi, separat pentru import și export. Pe alt calculator sau alt browser pornești de la configurația implicită.',
      en: 'Yes, automatically, in the browser you are using, separately for import and export. On another computer or browser you start from the default layout.',
    },
  },
  {
    id: 'notes-private',
    question: { ro: 'Cine vede notele pe care le scriu?', en: 'Who sees the notes I write?' },
    answer: {
      ro: 'Nota stă pe container sau pe B/L, deci o văd toate conturile din portal care au acces la aceeași firmă. Nota de pe B/L apare și echipei TLS, în fișa conosamentului. Pentru cereri urgente nu te baza pe note — scrie agentului tău.',
      en: 'A note is stored on the container or bill of lading, so every portal account with access to the same company sees it. The note on a bill of lading is also shown to the TLS team, on the bill’s card. Don’t rely on notes for urgent requests — write to your agent.',
    },
  },
  {
    id: 'phone-language',
    question: { ro: 'Cum schimb limba sau tema pe telefon?', en: 'How do I change the language or theme on a phone?' },
    answer: {
      ro: 'Butoanele de limbă și temă sunt în bara de sus pe ecranele mai late. Pe telefon, rotește ecranul în modul peisaj sau folosește un calculator; alegerea rămâne salvată.',
      en: 'The language and theme buttons are in the top bar on wider screens. On a phone, turn it to landscape or use a computer; your choice stays saved.',
    },
  },
  {
    id: 'new-login-alert',
    question: { ro: 'Am primit un email de conectare nouă. Ce fac?', en: 'I got a new sign-in email. What should I do?' },
    answer: {
      ro: 'Dacă nu ai fost tu, schimbă-ți parola din „Ai uitat parola?” de pe pagina de autentificare, activează 2FA și anunță agentul TLS.',
      en: 'If it wasn’t you, change your password with “Forgot password” on the sign-in page, turn on 2FA and tell your TLS agent.',
    },
  },
  {
    id: 'tour-again',
    question: { ro: 'Pot relua turul ghidat?', en: 'Can I replay the guided tour?' },
    answer: {
      ro: 'Da: butonul „Pornește turul ghidat” de pe această pagină sau „Reia turul ghidat” din meniul cu numele tău.',
      en: 'Yes: the “Start the guided tour” button on this page, or “Replay the guided tour” in the menu with your name.',
    },
  },
];

/** Alege textul în limba curentă. */
export function pick(text: Localized, lang: HelpLang): string {
  return text[lang] ?? text.ro;
}

/** Text normalizat pentru căutare: fără diacritice, litere mici. */
export function normalizeForSearch(value: string): string {
  return value
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase();
}

/** True dacă elementul e relevant pentru direcția curentă. */
export function matchesDirection(directions: ShippingDirection[] | undefined, direction: ShippingDirection): boolean {
  return !directions || directions.includes(direction);
}
